"use client";

import {
    DragDropContext,
    Draggable,
    DraggableProvided,
    Droppable,
    DropResult,
} from "@hello-pangea/dnd";
import { useEffect, useState } from "react";
import { updateHostOrder } from "~/app/events/[slug]/actions";
import { LinkWrapper } from "~/components/v4/shadcn/LinkWrapper";
import { UserAvatar } from "~/components/v4/shadcn/UserAvatar";
import { EventBySlugOutput } from "~/types/EventTypesInferred";
import { DeleteHostButton } from "./HostPageActions";
import { toast } from "react-toastify";
import { LuGripVertical, LuCopy, LuTrash2 } from "react-icons/lu";
import { useIsMobileV2 } from "~/hooks/useIsMobile";
import { cn } from "~/utils/fontsConfig";
import { removeHostPreapproval } from "./actions";
import { CustomizableLayout } from "~/types/CustomizableLayout";

const HostEntry = ({
    host,
    event,
    canDelete,
    provided,
    hostsCount,
    isMobile,
    customization,
}: {
    host: EventBySlugOutput["hosts"][number];
    event: { creatorId: string | null; id: string; slug: string };
    canDelete: boolean;
    provided: DraggableProvided;
    hostsCount: number;
    isMobile: boolean;
    customization?: CustomizableLayout;
}) => {
    const copyEmail = async (userEmail: string | null) => {
        if (!userEmail) return;
        await navigator.clipboard.writeText(userEmail);
        toast.success("Email copied to clipboard");
    };

    return (
        <div
            ref={provided.innerRef}
            {...(isMobile ? {} : provided.dragHandleProps)}
            {...provided.draggableProps}
            key={host.id}
            className={cn(
                "group grid grid-cols-1 items-center gap-4 rounded-[10px] bg-v3-N-500 px-4 py-3 xs:grid-cols-[1fr_1fr_auto]",
                customization?.secondaryCardClassName,
            )}
        >
            <LinkWrapper
                href={`/profile/${host.user.slug}`}
                className="flex items-center gap-3"
            >
                <UserAvatar user={host.user} className="h-10 w-10" />
                <div className="flex gap-2">
                    <span
                        className={cn(
                            "text-nowrap text-base text-white",
                            customization?.descriptionClassName,
                        )}
                    >
                        {host.user.name}
                    </span>
                    {host.user.id === event.creatorId && (
                        <span
                            className={cn(
                                "rounded-lg bg-v3-clover px-3 py-1 text-sm text-white",
                                customization?.descriptionClassName,
                            )}
                        >
                            Creator
                        </span>
                    )}
                </div>
            </LinkWrapper>

            <div
                className={cn(
                    "group/email hidden cursor-pointer items-center gap-2 justify-self-start md:flex",
                    customization?.descriptionClassName,
                )}
                onClick={() => copyEmail(host.user.email)}
            >
                <span>{host.user.email}</span>
                <LuCopy
                    className={cn(
                        "h-4 w-4 text-v3-N-300 opacity-0 transition-opacity group-hover/email:opacity-100",
                        customization?.descriptionClassName,
                    )}
                />
            </div>

            {canDelete && (
                <div className="flex items-center gap-4 xs:justify-end">
                    {hostsCount > 1 && (
                        <div
                            {...(isMobile ? provided.dragHandleProps : {})}
                            className={cn(
                                "cursor-grab px-1 transition-opacity",
                                !isMobile &&
                                    "opacity-0 group-hover:opacity-100",
                            )}
                        >
                            <LuGripVertical
                                className={cn(
                                    "h-4 w-4 text-v3-N-300",
                                    customization?.descriptionClassName,
                                )}
                            />
                        </div>
                    )}

                    <DeleteHostButton
                        eventId={event.id}
                        hostId={host.id}
                        hostUserId={host.user.id}
                        isCreator={host.user.id === event.creatorId}
                        isLastHost={hostsCount === 1}
                        eventSlug={event.slug}
                    />
                </div>
            )}
        </div>
    );
};

const PreapprovalEntry = ({
    host,
    eventId,
    canDelete,
}: {
    host: { id: string; email: string | null };
    eventId: string;
    canDelete: boolean;
}) => {
    const deletePreapproval = () => {
        removeHostPreapproval({
            eventId,
            hostPreapprovalId: host.id,
        });
    };

    return (
        <div
            key={host.id}
            className="group grid grid-cols-1 items-center gap-4 rounded-[10px] bg-v3-N-500 px-4 py-4 xs:grid-cols-[1fr_1fr_auto]"
        >
            <div className="flex items-center gap-3">
                <span className="rounded-lg bg-v3-clover px-3 py-1 text-sm text-white">
                    Invited
                </span>
            </div>
            <span>{host.email}</span>

            {canDelete && (
                <div className="flex items-center gap-4 xs:justify-end">
                    <button
                        onClick={deletePreapproval}
                        className="text-v3-N-300 hover:text-white"
                    >
                        <LuTrash2 className="h-5 w-5" />
                    </button>
                </div>
            )}
        </div>
    );
};

export const HostsList = ({
    hosts,
    preapprovals,
    event,
    canDelete,
    customization,
}: {
    hosts: EventBySlugOutput["hosts"];
    preapprovals: EventBySlugOutput["hostPreapprovals"];
    event: EventBySlugOutput;
    canDelete: boolean;
    customization?: CustomizableLayout;
}) => {
    const [isMobile, setIsMobile] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [orderedHosts, setOrderedHosts] = useState(hosts);
    const hasManagingPerms = true;

    useEffect(() => {
        setIsMobile(useIsMobileV2());
    }, []);

    useEffect(() => {
        // Sort hosts by order when they change
        setOrderedHosts(
            [...hosts].sort((a, b) => (a.order || 0) - (b.order || 0)),
        );
    }, [hosts]);

    const handleDragStart = () => {
        setIsDragging(true);
    };

    const handleDragEnd = async (result: DropResult) => {
        setIsDragging(false);

        if (!result.destination || !hasManagingPerms) return;

        const newOrderedHosts = [...orderedHosts];
        const [reorderedHost] = newOrderedHosts.splice(result.source.index, 1);
        newOrderedHosts.splice(result.destination.index, 0, reorderedHost!);

        // update local state immediately, optimistic update
        setOrderedHosts(newOrderedHosts);

        // Update order in database using server action
        try {
            await updateHostOrder({
                eventSlug: event.slug,
                hostOrders: newOrderedHosts.map((host, index) => ({
                    hostId: host.id,
                    order: index,
                })),
            });
        } catch (error) {
            console.error("Failed to update host order:", error);
            toast.error("Failed to update host order");
            // Revert to original order on error
            setOrderedHosts(hosts);
        }
    };

    return (
        <>
            <DragDropContext
                onDragEnd={handleDragEnd}
                onDragStart={handleDragStart}
            >
                <Droppable droppableId="orderedHosts">
                    {(provided) => (
                        <div
                            className={cn(
                                "flex flex-col gap-2",
                                customization?.mainCardClassName,
                            )}
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                        >
                            {orderedHosts.map((host, index) => (
                                <Draggable
                                    key={host.id}
                                    draggableId={host.id}
                                    index={index}
                                    isDragDisabled={
                                        !hasManagingPerms ||
                                        orderedHosts.length <= 1
                                    }
                                >
                                    {(provided, snapshot) => (
                                        <HostEntry
                                            host={host}
                                            event={event}
                                            canDelete={canDelete || false}
                                            provided={provided}
                                            hostsCount={hosts.length}
                                            isMobile={isMobile}
                                            customization={customization}
                                        />
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
            {preapprovals.map((host, index) => (
                <PreapprovalEntry
                    host={host}
                    eventId={event.id}
                    canDelete={canDelete || false}
                />
            ))}
        </>
    );
};
